import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../api/axiosInstance";

const TwoFactorAuthentication = () => {
  const [activeTab, setActiveTab] = useState("2fa");

  // Change password UI (static for now)
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState({
    old: "",
    next: "",
    confirm: "",
  });

  // Telegram 2FA UI
  const [loginPassword, setLoginPassword] = useState("");
  const [connectionId, setConnectionId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);

  const passwordMeetsRules = useMemo(() => {
    if (!newPassword || newPassword.length < 6) return false;
    const hasUpper = /[A-Z]/.test(newPassword);
    const hasLower = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    return hasUpper && hasLower && hasNumber;
  }, [newPassword]);

  const handleChangePasswordSubmit = (event) => {
    event.preventDefault();

    const nextErrors = {
      old: "",
      next: "",
      confirm: "",
    };

    if (!currentPassword) {
      nextErrors.old = "The Old Password field is required";
    }

    if (!passwordMeetsRules) {
      nextErrors.next =
        "The New Password must be at least 6 characters and contain 1 uppercase, 1 lowercase letter, 1 number";
    }

    if (newPassword !== confirmPassword) {
      nextErrors.confirm = "The Confirm Password confirmation does not match";
    }

    if (nextErrors.old || nextErrors.next || nextErrors.confirm) {
      setPasswordErrors(nextErrors);
      toast.error("Please fix the errors in the form.");
      return;
    }

    setPasswordErrors({
      old: "",
      next: "",
      confirm: "",
    });

    // Static UI only (no API wire-up requested yet)
    toast.success("Change password UI ready (API hookup pending).");
  };

  const handleGenerateTelegramConnectionId = async () => {
    if (loading) return;
    if (!loginPassword || !loginPassword.trim()) {
      setError("Please enter your login password.");
      toast.error("Please enter your login password.");
      return;
    }

    setLoading(true);
    setError("");
    setConnectionId("");
    setShowInstructions(false);

    try {
      const response = await axiosInstance.post(
        "/telegram/generateTelegramConnectionId",
        {
          password: loginPassword,
        },
      );

      // axiosInstance returns response.data directly
      const data = response;
      const generatedId =
        data?.connectionId ||
        data?.connection_id ||
        data?.data?.connectionId ||
        data?.data?.connection_id ||
        "";

      if (!generatedId) {
        setError("Unable to generate connection ID. Please try again.");
        toast.error("Unable to generate connection ID. Please try again.");
        return;
      }

      setConnectionId(generatedId);
      setShowInstructions(true);
      toast.success("Connection ID generated.");
    } catch (e) {
      const message =
        typeof e === "string"
          ? e
          : e?.response?.data?.message ||
            e?.message ||
            "Failed to generate Telegram connection ID.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyConnectionId = async () => {
    if (!connectionId) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(connectionId);
      } else {
        const el = document.createElement("textarea");
        el.value = connectionId;
        el.setAttribute("readonly", "");
        el.style.position = "absolute";
        el.style.left = "-9999px";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      toast.success("Copied");
    } catch {
      toast.error("Unable to copy. Please copy manually.");
    }
  };

  const handleCopyConnectCommand = async () => {
    if (!connectionId) return;
    const cmd = `/connect ${connectionId}`;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(cmd);
      } else {
        const el = document.createElement("textarea");
        el.value = cmd;
        el.setAttribute("readonly", "");
        el.style.position = "absolute";
        el.style.left = "-9999px";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      toast.success("Copied");
    } catch {
      toast.error("Unable to copy. Please copy manually.");
    }
  };

  return (
    <section className="section profile">
      <div className="row g-0">
        <div className="col-lg-12">
          <h2 className="userscreen-title">2FA Settings</h2>
        </div>

        {/* left */}
        <div className="col-xl-4 mb-3 mb-xl-0">
          <div className="card">
            <div className="card-body pt-4">
              <div className="tab-container">
                <ul
                  role="tablist"
                  className="nav nav-tab nav-tabs-bordered flex-column"
                  aria-label="2FA Tabs"
                >
                  <li
                    className={`nav-item pt-2 ${activeTab === "2fa" ? "active" : ""}`}
                  >
                    <button
                      type="button"
                      className={`nav-link w-100 text-start ${
                        activeTab === "2fa" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("2fa")}
                    >
                      <i className="bi bi-shield-lock me-2" />
                      2FA Authentication
                    </button>
                  </li>
                  <li
                    className={`nav-item pt-2  ${activeTab === "password" ? "active" : ""}`}
                  >
                    <button
                      type="button"
                      className={`nav-link w-100 text-start ${
                        activeTab === "password" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("password")}
                    >
                      <i className="bi bi-key me-2" />
                      Change Password
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* right */}
        <div className="col-xl-8">
          <div className="card">
            <div className="card-body">
              {activeTab === "password" && (
                <form
                  noValidate
                  className="cp_form"
                  onSubmit={handleChangePasswordSubmit}
                >
                  <div className="row mb-lg-3 mt-lg-2 align-items-center">
                    <label
                      htmlFor="currentPassword"
                      className="col-md-3 col-lg-3 col-form-label text-lg"
                    >
                      Current Password :
                    </label>
                    <div className="col-md-9 col-lg-9">
                      <input
                        type="password"
                        id="currentPassword"
                        className="form-control"
                        value={currentPassword}
                        onChange={(e) => {
                          setCurrentPassword(e.target.value);
                          if (passwordErrors.old) {
                            setPasswordErrors((prev) => ({ ...prev, old: "" }));
                          }
                        }}
                      />
                      {passwordErrors.old && (
                        <p className="text-danger small m-0">
                          {passwordErrors.old}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="row mb-lg-3 mt-lg-2 align-items-center">
                    <label
                      htmlFor="newPassword"
                      className="col-md-3 col-lg-3 col-form-label text-lg"
                    >
                      New Password :
                    </label>
                    <div className="col-md-9 col-lg-9">
                      <input
                        type="password"
                        id="newPassword"
                        className="form-control"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          if (passwordErrors.next) {
                            setPasswordErrors((prev) => ({
                              ...prev,
                              next: "",
                            }));
                          }
                        }}
                      />
                      {passwordErrors.next && (
                        <p className="text-danger small m-0">
                          {passwordErrors.next}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="row mb-lg-3 mt-lg-2 align-items-center">
                    <label
                      htmlFor="renewPassword"
                      className="col-md-3 col-lg-3 col-form-label text-lg"
                    >
                      Re-enter New Password :
                    </label>
                    <div className="col-md-9 col-lg-9">
                      <input
                        type="password"
                        id="renewPassword"
                        className="form-control"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (passwordErrors.confirm) {
                            setPasswordErrors((prev) => ({
                              ...prev,
                              confirm: "",
                            }));
                          }
                        }}
                      />
                      {passwordErrors.confirm && (
                        <p className="text-danger small m-0">
                          {passwordErrors.confirm}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="feedback">
                    <p className="small m-0">
                      <i>
                        <b>Note:</b> The New Password field must be at least 6
                        characters
                      </i>
                    </p>
                    <p className="small m-0">
                      <i>
                        <b>Note:</b> The New Password must contain at least: 1
                        uppercase letter, 1 lowercase letter, 1 number
                      </i>
                    </p>
                  </div>

                  <div className="row">
                    <button type="submit" className="btn btn-thmemes">
                      Change Password
                    </button>
                  </div>
                </form>
              )}

              {activeTab === "2fa" && (
                <div className="cp_form">
                  <div className="row mb-lg-3 mt-lg-2 align-items-center">
                    <label
                      htmlFor="twoFaPassword"
                      className="col-md-3 col-lg-3 col-form-label text-lg"
                    >
                      Password :
                    </label>
                    <div className="col-md-9 col-lg-9">
                      <input
                        type="password"
                        id="twoFaPassword"
                        className="form-control"
                        value={loginPassword}
                        onChange={(e) => {
                          setLoginPassword(e.target.value);
                          if (error) setError("");
                          if (connectionId) setConnectionId("");
                          if (showInstructions) setShowInstructions(false);
                        }}
                      />
                      {error && (
                        <p className="text-danger small m-0">{error}</p>
                      )}
                    </div>
                  </div>

                  {!showInstructions ? (
                    <>
                      <div className="row mt-3">
                        <button
                          type="button"
                          className="btn btn-thmemes"
                          disabled={loading}
                          onClick={handleGenerateTelegramConnectionId}
                        >
                          {loading
                            ? "Generating..."
                            : "Get Telegram Connection ID"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mt-3">
                        <div className="alert alert-success mb-0">
                          <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                            <div>
                              <b>Connection ID:</b> {connectionId}
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success"
                              onClick={handleCopyConnectionId}
                              aria-label="Copy connection ID"
                              title="Copy"
                            >
                              <i className="bi bi-clipboard" />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <div className="alert alert-light border mb-0">
                          <p className="mb-2 fw-bold">
                            Please follow below instructions for the telegram
                            2-step verification
                          </p>

                          <div className="mb-2 d-flex gap-2">
                            <span className="badge bg-secondary">1</span>
                            <div>
                              Find{" "}
                              <a
                                href="https://t.me/Tenexch9_bot"
                                target="_blank"
                                rel="noreferrer"
                              >
                                @Tenexch9_bot
                              </a>{" "}
                              in your telegram and type{" "}
                              <code className="px-2 py-1 bg-dark text-white rounded">
                                /start
                              </code>{" "}
                              command. Bot will respond you.
                            </div>
                          </div>

                          <div className="mb-2 d-flex gap-2">
                            <span className="badge bg-secondary">2</span>
                            <div className="flex-grow-1">
                              After this type{" "}
                              <span className="d-inline-flex align-items-center gap-2">
                                <code className="px-2 py-1 bg-dark text-white rounded">
                                  /connect {connectionId}
                                </code>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={handleCopyConnectCommand}
                                  aria-label="Copy /connect command"
                                  title="Copy command"
                                >
                                  <i className="bi bi-clipboard" />
                                </button>
                              </span>{" "}
                              and send it to BOT.
                            </div>
                          </div>

                          <div className="mb-3 d-flex gap-2">
                            <span className="badge bg-secondary">3</span>
                            <div>
                              Now your telegram account will be linked with your
                              website account and 2-Step verification will be
                              enabled.
                            </div>
                          </div>

                          <hr className="my-3" />

                          <div className="small fst-italic">
                            कृपया टेलीग्राम 2-Step verification के लिए निर्देशों
                            का पालन करें। @Tenexch9_bot खोजें और "/start" भेजें,
                            फिर "/connect {connectionId}" भेजें।
                          </div>

                          <div className="mt-3">
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              onClick={() => setShowInstructions(false)}
                            >
                              Back to setup
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TwoFactorAuthentication;
